import type {
  AdConfig,
  AdCreative,
  CampaignObjective,
  MetaAdDraft,
  MetaAdDraftInput,
  MetaCallToAction,
  PlacementConfig,
  PublisherPlatform,
  TargetingConfig,
} from '../types/metaAdDraft.js';
import {
  CITY_RADIUS_BOUNDS,
  CUSTOM_LOCATION_RADIUS_BOUNDS,
  PLACE_RADIUS_BOUNDS,
} from './metaAdTargetingShape.js';
import { validOptimizationGoals } from './metaAdObjectiveShape.js';
import { isFbMePlaceholder, mediaModeOf, modeIsAvailable, reusableMedia } from './metaAttachMedia.js';
import { resolveAdSetDates } from './metaAdLegacyView.js';
import { effectiveScheduleTimeZone, wallClockToInstantMs } from './scheduleTime.js';
import {
  PLACEMENT_DEPENDENCIES,
  PLATFORMS,
  hasAnyStoryPosition,
  placementCounts,
  positionArrayKey,
} from './placementMatrix.js';

// Stage 7A (A2) — Meta Ad Builder STRUCTURAL validation. PURE: no I/O, no Zernio,
// no Firestore, no ambient Date (now is injected). Two entry points:
//
//   validateMetaAdDraft(draft, ctx) — the pre-publish gate. ok=false blocks the
//     publish call. Covers ONLY rules that can't be wrong regardless of Meta's
//     live state (required-field matrix, enum membership, numeric bounds,
//     cross-field consistency). Currency-specific budget minimums, audience-size
//     minimums, permission + page-IG-pairing errors are DEFERRED to Zernio and
//     surface as a PublishError at the publish boundary (Phase D), never here.
//
//   validateForCompose(input) — a softer autosave-time checklist. Never blocks;
//     returns warnings only ("what's still needed"). accountId may be null.
//
// MULTI-AD-SET: campaign-wide-locked rules (objective × destination, optimization
// goal, placements, page, pixel, shared creative, CBO budget) run ONCE; per-ad-set
// rules (targeting, ABO budget, date overrides) run PER ad set and attach to that
// ad set via `adSetId` so the UI can render them on the right card.
//
// Why ctx (not pure-from-draft): two rules need data the draft alone doesn't
// carry — "the referenced Account has fbAdAccountId" (cross-entity; caller loads
// the Account) and "endDate is in the future" (needs the current instant). Both
// are injected so the function stays pure + deterministically testable.

export type ValidationSeverity = 'error' | 'warning';

export type MetaAdIssueCode =
  // goal × destination matrix
  | 'destination_mismatch'
  | 'traffic_requires_website'
  | 'leadgenform_required'
  | 'url_required'
  | 'url_invalid'
  // page
  | 'page_required'
  // pixel (C8 — required for conversion tracking on both Lead Gen + Traffic)
  | 'pixel_required'
  // optimization goal + custom event type (D2.17)
  | 'optimization_goal_invalid'
  | 'custom_event_type_required'
  | 'custom_event_type_not_allowed'
  // creative (multi-input, C7-extension)
  | 'headlines_empty'
  | 'headlines_too_many'
  | 'headline_too_long'
  | 'primary_text_empty'
  | 'primary_text_too_many'
  | 'primary_text_too_long'
  | 'primary_text_long' // warning: over the recommended length
  | 'description_too_many'
  | 'description_too_long'
  | 'cta_invalid'
  | 'asset_missing'
  | 'asset_type_mismatch'
  | 'story_creative_invalid'
  | 'story_asset_type_mismatch' // D3.4: Story asset type ≠ Feed asset type (no image+video mix)
  // ad sets
  | 'adsets_empty' // multi-ad-set: a campaign needs at least one ad set
  // targeting
  | 'targeting_locked' // informational: extracted targeting is read-only (Meta shape authoritative)
  | 'age_min_too_low'
  | 'age_max_too_high'
  | 'age_range_inverted'
  | 'geo_required'
  | 'gender_invalid'
  | 'city_radius_out_of_bounds' // a city radius outside Meta's per-unit band (km 17–80, mi 10–50)
  | 'custom_location_invalid_coords' // a pin's lat/long outside valid ranges
  | 'custom_location_radius_out_of_bounds' // a pin radius outside the custom-location band
  | 'place_radius_out_of_bounds' // a place radius outside the place band (1–80 km)
  // placements
  | 'placements_manual_requires_config'
  | 'placements_empty_selection'
  | 'placements_platform_mismatch'
  | 'placements_dependency_unmet'
  | 'placements_story_without_image' // D3.1: Story placements selected, no Story image (compose warning)
  // schedule
  | 'start_date_in_past'
  | 'end_date_in_past'
  | 'end_date_before_start'
  // budget
  | 'budget_required'
  | 'budget_not_positive'
  | 'budget_not_integer'
  | 'budget_suspiciously_high'
  | 'budget_mismatch_cbo'
  | 'budget_mismatch_abo'
  | 'lifetime_requires_end_date'
  // account binding (pre-publish)
  | 'account_required'
  | 'account_not_connected'
  | 'account_ad_account_drift' // fbAdAccountId ≠ the Zernio profile's scoped ad account
  // Phase 2 — multiple ads per ad set (sequential attach)
  | 'additional_ad_name_required'
  | 'additional_ad_asset_missing'
  | 'additional_ad_thumbnail_required' // Phase 2a: video entries need an explicit thumbnail (no auto-extraction on attach)
  | 'additional_ad_headline_empty'
  | 'additional_ad_primary_text_empty'
  | 'additional_ad_url_required'
  | 'dco_with_additional_ads'
  | 'too_many_ads'
  // "Add an ad to a live ad set"
  | 'attach_target_missing'
  | 'attach_destination_mismatch'
  | 'attach_destination_unknown'
  | 'attach_media_unavailable'
  | 'attach_media_type_mismatch'
  | 'url_placeholder'
  | 'attach_single_ad_only'
  | 'ad_name_required';

export type MetaAdValidationIssue = {
  field: string; // dot-path locator, e.g. 'ads.creative.headlines' (UI scroll-to-error)
  code: MetaAdIssueCode;
  severity: ValidationSeverity;
  message: string; // human-readable; never leaks a stack/path/doc-ref
  // Multi-ad-set: set on PER-AD-SET issues to the owning AdSet.id so the UI can
  // render them on that ad set's card. Undefined for campaign-wide issues.
  adSetId?: string;
};

export type ValidationResult = {
  ok: boolean; // === (errors.length === 0); warnings may still be present
  errors: MetaAdValidationIssue[];
  warnings: MetaAdValidationIssue[];
};

// Semantic alias — validateForCompose only ever yields severity 'warning'.
export type ValidationWarnings = MetaAdValidationIssue[];

export type MetaAdValidationContext = {
  // The loaded kyck Account for draft.accountId (caller fetches it). null when
  // the account couldn't be resolved — treated as not-connected at publish.
  account: { fbAdAccountId: string | null } | null;
  now: Date; // injected current instant for the future-end-date check
  // The AD ACCOUNT's IANA timezone (same source as the city-search scope). The
  // schedule wall-clock resolves against this — or Australian Eastern when the
  // campaign's scheduleUseAustralianEastern override is on — for the future/order
  // checks. Optional: when absent the wall-clock is interpreted as UTC (the
  // order check is tz-independent; only the future check shifts).
  scheduleTimezone?: string | null;
};

// Runtime guards — the data comes from Firestore, so we can't trust the compile
// time union (a stale/hand-edited doc could carry a bad value).
const VALID_CTAS: ReadonlySet<MetaCallToAction> = new Set([
  'LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'BOOK_TRAVEL', 'CONTACT_US', 'DOWNLOAD',
  'GET_OFFER', 'GET_QUOTE', 'SUBSCRIBE', 'WATCH_MORE', 'REGISTER', 'JOIN',
  'ATTEND', 'REQUEST_DEMO', 'VIEW_QUOTE', 'APPLY', 'SEE_MORE', 'BUY_NOW',
]);

const VALID_GENDERS: ReadonlySet<string> = new Set(['all', 'male', 'female']);

const AGE_MIN = 13;
const AGE_MAX = 65;
// Typo guard only — a real per-currency minimum is Zernio's job. $1,000,000.00.
const BUDGET_SUSPICIOUSLY_HIGH_CENTS = 100_000_000;

function nonEmpty(s: string | null | undefined): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

// Structural URL check — http/https + parseable. Pure (no network).
function isValidHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function issue(
  field: string,
  code: MetaAdIssueCode,
  severity: ValidationSeverity,
  message: string,
): MetaAdValidationIssue {
  return { field, code, severity, message };
}

// Stamp a batch of per-ad-set issues with the owning ad set's id (+ field prefix
// already baked into each `field`).
function withAdSetId(issues: MetaAdValidationIssue[], adSetId: string): MetaAdValidationIssue[] {
  return issues.map((i) => ({ ...i, adSetId }));
}

// ---------- shared rule bodies (publish severity) ----------

// objective × destination consistency (D2.17). objective ∈ {TRAFFIC, LEADS}; the
// ad's destination is the discriminated union.
function checkGoalDestination(
  objective: CampaignObjective,
  destinationType: 'instant_form' | 'website',
  destination: AdConfig['destination'],
): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];

  // Traffic can only go to a website.
  if (objective === 'TRAFFIC' && destinationType !== 'website') {
    out.push(
      issue(
        'campaign.destinationType',
        'traffic_requires_website',
        'error',
        'Traffic campaigns must use a Website destination.',
      ),
    );
  }

  // The ad's destination must agree with the campaign destination type.
  if (destination.type !== destinationType) {
    out.push(
      issue(
        'ads.destination.type',
        'destination_mismatch',
        'error',
        "The ad's destination doesn't match the campaign's destination type.",
      ),
    );
  }

  // Per-destination required field.
  if (destination.type === 'instant_form') {
    if (!nonEmpty(destination.leadGenFormId)) {
      out.push(
        issue(
          'ads.destination.leadGenFormId',
          'leadgenform_required',
          'error',
          'Select a lead form for the Instant Form destination.',
        ),
      );
    }
  } else {
    if (!nonEmpty(destination.url)) {
      out.push(
        issue('ads.destination.url', 'url_required', 'error', 'Enter a destination URL.'),
      );
    } else if (!isValidHttpUrl(destination.url)) {
      out.push(
        issue('ads.destination.url', 'url_invalid', 'error', 'Enter a valid http(s) URL.'),
      );
    }
  }

  return out;
}

// D2.17 — the campaign optimization goal must be valid for the objective+destination,
// and customEventType is required iff OFFSITE_CONVERSIONS (forbidden otherwise).
// Pixel is already required for every draft (C8), so it isn't re-checked here.
function checkOptimizationGoal(
  objective: CampaignObjective,
  destinationType: 'instant_form' | 'website',
  optimizationGoal: string,
  customEventType: string | null,
): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];

  if (!validOptimizationGoals(objective, destinationType).includes(optimizationGoal)) {
    out.push(
      issue(
        'campaign.optimizationGoal',
        'optimization_goal_invalid',
        'error',
        'Choose an optimization goal valid for this objective and destination.',
      ),
    );
  }

  if (optimizationGoal === 'OFFSITE_CONVERSIONS') {
    if (!nonEmpty(customEventType)) {
      out.push(
        issue('campaign.customEventType', 'custom_event_type_required', 'error', 'Choose an optimization event.'),
      );
    }
  } else if (nonEmpty(customEventType)) {
    out.push(
      issue(
        'campaign.customEventType',
        'custom_event_type_not_allowed',
        'error',
        'An optimization event only applies to Conversions optimization.',
      ),
    );
  }

  return out;
}

// Copy multi-input caps (C7-extension). Each is a soft Meta limit; >hard is a
// hard error, >recommended is a warning.
const HEADLINE_MAX = 40;
const PRIMARY_TEXT_RECOMMENDED = 125;
// Meta's ACTUAL primary-text (`message`) ceiling is 2,200 characters — the field
// stores up to 2,200; display just truncates ~125 chars behind "See more". The
// old 900 was an invented, too-conservative cap that hard-blocked publishes Meta
// accepts fine. 125 stays as a soft (warning) recommendation, not a block.
const PRIMARY_TEXT_HARD = 2200;
const DESCRIPTION_MAX = 30;
const COPY_MAX_ITEMS = 4;
// Phase 1 Dynamic Creative: headlines/primaryTexts cap at Meta's 5-variation
// limit (the UI only exposes >1 when useDynamicCreative). Descriptions keep 4.
const TEXT_VARIATION_MAX_ITEMS = 5;

function checkCreative(creative: AdCreative): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];

  // Headlines — 1..5, each non-empty ≤40.
  const headlines = creative.headlines ?? [];
  if (headlines.filter((h) => nonEmpty(h)).length < 1) {
    out.push(issue('ads.creative.headlines', 'headlines_empty', 'error', 'Add at least one headline.'));
  }
  if (headlines.length > TEXT_VARIATION_MAX_ITEMS) {
    out.push(issue('ads.creative.headlines', 'headlines_too_many', 'error', `Up to ${TEXT_VARIATION_MAX_ITEMS} headlines.`));
  }
  headlines.forEach((h, i) => {
    if (h.trim().length > HEADLINE_MAX) {
      out.push(issue(`ads.creative.headlines.${i}`, 'headline_too_long', 'error', `Headline ${i + 1} is over ${HEADLINE_MAX} characters.`));
    }
  });

  // Primary texts — 1..5, each non-empty; >2200 (Meta's real cap) hard error,
  // >125 recommended warning.
  const primaryTexts = creative.primaryTexts ?? [];
  if (primaryTexts.filter((p) => nonEmpty(p)).length < 1) {
    out.push(issue('ads.creative.primaryTexts', 'primary_text_empty', 'error', 'Add at least one primary text.'));
  }
  if (primaryTexts.length > TEXT_VARIATION_MAX_ITEMS) {
    out.push(issue('ads.creative.primaryTexts', 'primary_text_too_many', 'error', `Up to ${TEXT_VARIATION_MAX_ITEMS} primary texts.`));
  }
  primaryTexts.forEach((p, i) => {
    const len = p.trim().length;
    if (len > PRIMARY_TEXT_HARD) {
      out.push(issue(`ads.creative.primaryTexts.${i}`, 'primary_text_too_long', 'error', `Primary text ${i + 1} is over ${PRIMARY_TEXT_HARD} characters.`));
    } else if (len > PRIMARY_TEXT_RECOMMENDED) {
      out.push(issue(`ads.creative.primaryTexts.${i}`, 'primary_text_long', 'warning', `Primary text ${i + 1} is over the recommended ${PRIMARY_TEXT_RECOMMENDED} characters.`));
    }
  });

  // Descriptions — 0..4, each non-empty ≤30.
  const descriptions = creative.descriptions ?? [];
  if (descriptions.length > COPY_MAX_ITEMS) {
    out.push(issue('ads.creative.descriptions', 'description_too_many', 'error', `Up to ${COPY_MAX_ITEMS} descriptions.`));
  }
  descriptions.forEach((d, i) => {
    if (d.trim().length > DESCRIPTION_MAX) {
      out.push(issue(`ads.creative.descriptions.${i}`, 'description_too_long', 'error', `Description ${i + 1} is over ${DESCRIPTION_MAX} characters.`));
    }
  });

  if (!VALID_CTAS.has(creative.callToAction)) {
    out.push(
      issue('ads.creative.callToAction', 'cta_invalid', 'error', 'Choose a call-to-action.'),
    );
  }

  // Feed asset (D3.4): required, image XOR video, type matching assetType. The
  // video thumbnail is OPTIONAL now (Meta auto-generates from the first frame).
  const feed = creative.feedAsset;
  const feedPopulated = assetPopulated(feed);
  if (!feedPopulated) {
    out.push(
      issue(
        'ads.creative.feedAsset',
        'asset_missing',
        'error',
        creative.assetType === 'video' ? 'Upload a Feed video.' : 'Upload a Feed image.',
      ),
    );
  } else if (feed.type && feed.type !== creative.assetType) {
    out.push(
      issue(
        'ads.creative.assetType',
        'asset_type_mismatch',
        'error',
        "The Feed asset type doesn't match the uploaded file.",
      ),
    );
  }

  return out;
}

// Story asset slot (D3.4 — image OR video, was image-only). Optional: returns []
// when empty. When populated it must carry a durable path AND match the Feed
// asset type (Meta can't mix image + video in one placementAssets block).
/** An uploaded asset (url + path) or media reused from a live ad (image hash / video id, clones). */
function assetPopulated(a: { url: string | null; path: string | null; imageHash?: string | null; videoId?: string | null }): boolean {
  return (nonEmpty(a.url) && nonEmpty(a.path)) || nonEmpty(a.imageHash) || nonEmpty(a.videoId);
}

function checkStoryCreative(creative: AdCreative): MetaAdValidationIssue[] {
  const story = creative.storyAsset;
  if (!nonEmpty(story.url)) return []; // empty slot — genuinely optional
  const out: MetaAdValidationIssue[] = [];

  if (!nonEmpty(story.path)) {
    out.push(issue('ads.creative.storyAsset', 'story_creative_invalid', 'error', 'Upload a Story asset.'));
  }
  if (story.type && story.type !== creative.assetType) {
    out.push(
      issue(
        'ads.creative.storyAsset',
        'story_asset_type_mismatch',
        'error',
        'The Story asset must be the same type (image or video) as the Feed asset.',
      ),
    );
  }

  return out;
}

// Unified-search hierarchy model: geographic layers are NO LONGER mutually exclusive
// (a state in one country can coexist with specific areas in another — the geographic-
// hierarchy replacement on selection prevents an area + its own containing broader area).
// So this just requires AT LEAST ONE geo selection of any kind, plus the per-unit radius
// bounds. Shared by the publish gate ('error') and the compose checklist ('warning').
// Callers must NOT run this for extracted targeting (it's locked to its raw shape).
function checkGeoLevel(
  t: TargetingConfig,
  severity: ValidationSeverity,
  fieldBase: string,
): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];

  // At least one geo selection of ANY type (the hierarchy logic owns mutual exclusion).
  const hasAnyGeo =
    t.countries.length > 0 ||
    t.regions.length > 0 ||
    t.cities.length > 0 ||
    (Array.isArray(t.places) && t.places.length > 0) ||
    (Array.isArray(t.neighborhoods) && t.neighborhoods.length > 0) ||
    (Array.isArray(t.zips) && t.zips.length > 0) ||
    (Array.isArray(t.customLocations) && t.customLocations.length > 0);
  if (!hasAnyGeo) {
    out.push(issue(fieldBase, 'geo_required', severity, 'Add at least one location (search a place, or drop a map pin).'));
  }

  // Radius + coordinate bands are validated for BOTH the included geo AND the manual
  // exclusions (an out-of-band excluded radius is just as invalid at publish). geo_required
  // above intentionally counts INCLUDED geo only — an exclusion alone is not an audience.
  pushGeoRadiusIssues(t.cities, t.places, t.customLocations, severity, fieldBase, out);
  const e = t.excluded;
  if (e) pushGeoRadiusIssues(e.cities, e.places, e.customLocations, severity, fieldBase, out);
  return out;
}

// Per-bucket radius/coordinate band checks (cities 17–80km / mi 10–50; places + pins
// 1–80km / mi 1–50; pin coords in range). The map clamps on edit, so these only fire on
// a hand-edited / API-written value. Shared by the included + excluded geo buckets.
function pushGeoRadiusIssues(
  cities: TargetingConfig['cities'] | undefined,
  places: TargetingConfig['places'] | undefined,
  customLocations: TargetingConfig['customLocations'] | undefined,
  severity: ValidationSeverity,
  fieldBase: string,
  out: MetaAdValidationIssue[],
): void {
  // City radius must be within Meta's per-unit band. radius 0 = "no explicit radius".
  for (const c of Array.isArray(cities) ? cities : []) {
    const unit = c.distanceUnit === 'mile' ? 'mile' : 'kilometer';
    const { min, max } = CITY_RADIUS_BOUNDS[unit];
    if (typeof c.radius === 'number' && c.radius > 0 && (c.radius < min || c.radius > max)) {
      out.push(
        issue(fieldBase, 'city_radius_out_of_bounds', severity, `City radius must be ${min}–${max} ${unit === 'mile' ? 'mi' : 'km'}.`),
      );
    }
  }

  // Place radius band (1–80 km).
  for (const p of Array.isArray(places) ? places : []) {
    const unit = p.distanceUnit === 'mile' ? 'mile' : 'kilometer';
    const { min, max } = PLACE_RADIUS_BOUNDS[unit];
    if (typeof p.radius === 'number' && p.radius > 0 && (p.radius < min || p.radius > max)) {
      out.push(
        issue(fieldBase, 'place_radius_out_of_bounds', severity, `Place radius must be ${min}–${max} ${unit === 'mile' ? 'mi' : 'km'}.`),
      );
    }
  }

  // Custom-pin coordinates + radius band.
  for (const c of Array.isArray(customLocations) ? customLocations : []) {
    const latOk = Number.isFinite(c.latitude) && c.latitude >= -90 && c.latitude <= 90;
    const lngOk = Number.isFinite(c.longitude) && c.longitude >= -180 && c.longitude <= 180;
    if (!latOk || !lngOk) {
      out.push(issue(fieldBase, 'custom_location_invalid_coords', severity, 'A map pin has invalid coordinates.'));
    }
    const unit = c.distanceUnit === 'mile' ? 'mile' : 'kilometer';
    const { min, max } = CUSTOM_LOCATION_RADIUS_BOUNDS[unit];
    if (!Number.isFinite(c.radius) || c.radius < min || c.radius > max) {
      out.push(
        issue(fieldBase, 'custom_location_radius_out_of_bounds', severity, `Map pin radius must be ${min}–${max} ${unit === 'mile' ? 'mi' : 'km'}.`),
      );
    }
  }
}

function checkTargeting(t: TargetingConfig, fieldBase: string): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];

  if (t.ageMin < AGE_MIN) {
    out.push(
      issue(`${fieldBase}.ageMin`, 'age_min_too_low', 'error', `Minimum age is ${AGE_MIN}.`),
    );
  }
  if (t.ageMax > AGE_MAX) {
    out.push(
      issue(`${fieldBase}.ageMax`, 'age_max_too_high', 'error', `Maximum age is ${AGE_MAX}.`),
    );
  }
  if (t.ageMin > t.ageMax) {
    out.push(
      issue(
        `${fieldBase}.ageMin`,
        'age_range_inverted',
        'error',
        'Minimum age must be at or below the maximum age.',
      ),
    );
  }
  out.push(...checkGeoLevel(t, 'error', fieldBase));
  if (!VALID_GENDERS.has(t.gender)) {
    out.push(issue(`${fieldBase}.gender`, 'gender_invalid', 'error', 'Choose a gender.'));
  }

  return out;
}

// Placement consistency (manual mode only — 'automatic' is always valid). Now a
// campaign-wide-locked config.
function checkPlacements(placements: PlacementConfig): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];
  if (placements.mode !== 'manual') return out;

  const cfg = placements.manualConfig;
  if (!cfg) {
    out.push(
      issue(
        'campaign.placements.manualConfig',
        'placements_manual_requires_config',
        'error',
        'Select placements, or switch to Automatic.',
      ),
    );
    return out;
  }

  // (D3.1) Total across all four platforms.
  if (placementCounts(cfg).total === 0) {
    out.push(
      issue(
        'campaign.placements.manualConfig',
        'placements_empty_selection',
        'error',
        'Choose at least one placement.',
      ),
    );
  }

  // Every platform with ≥1 selected position must be listed in publisherPlatforms.
  for (const pf of PLATFORMS) {
    const positions = cfg[positionArrayKey(pf.value)] as string[];
    if (positions.length > 0 && !cfg.publisherPlatforms.includes(pf.value)) {
      out.push(
        issue(
          'campaign.placements.manualConfig.publisherPlatforms',
          'placements_platform_mismatch',
          'error',
          `${pf.label} placements need ${pf.label} enabled as a platform.`,
        ),
      );
    }
  }

  // Meta co-selection dependencies (e.g. IG Explore Home requires Explore).
  for (const [dependent, requirements] of Object.entries(PLACEMENT_DEPENDENCIES)) {
    const [dp, dv] = dependent.split(':');
    if (!dp || !dv) continue;
    const depPositions = cfg[positionArrayKey(dp as PublisherPlatform)] as string[];
    if (!depPositions.includes(dv)) continue;
    for (const req of requirements) {
      const [rp, rv] = req.split(':');
      if (!rp || !rv) continue;
      const reqPositions = cfg[positionArrayKey(rp as PublisherPlatform)] as string[];
      if (!reqPositions.includes(rv)) {
        out.push(
          issue(
            'campaign.placements.manualConfig',
            'placements_dependency_unmet',
            'error',
            `${dv} placement requires ${rv} to also be selected.`,
          ),
        );
      }
    }
  }

  return out;
}

// ---------- Phase 2 — additional ads (sequential attach), on the shared `ads` ----------
const MAX_ADS_PER_AD_SET = 10;

function checkAdditionalAds(ad: AdConfig, isLeadGen: boolean): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];
  const ads = ad.additionalAds ?? [];
  if (ads.length === 0) return out;

  if (ad.creative.useDynamicCreative) {
    out.push(
      issue(
        'ads.additionalAds',
        'dco_with_additional_ads',
        'error',
        'Dynamic Creative can’t be combined with multiple ads — Meta allows one ad per Dynamic Creative ad set.',
      ),
    );
  }
  if (ads.length + 1 > MAX_ADS_PER_AD_SET) {
    out.push(
      issue(
        'ads.additionalAds',
        'too_many_ads',
        'error',
        `Up to ${MAX_ADS_PER_AD_SET} ads per ad set (1 primary + ${MAX_ADS_PER_AD_SET - 1} additional).`,
      ),
    );
  }

  const sharedHeadline = (ad.creative.headlines ?? []).find((h) => nonEmpty(h)) ?? '';
  const sharedPrimaryText = (ad.creative.primaryTexts ?? []).find((p) => nonEmpty(p)) ?? '';
  const sharedUrl = ad.destination.type === 'website' ? ad.destination.url : '';

  ads.forEach((entry, i) => {
    const field = `ads.additionalAds.${i}`;
    const label = entry.name.trim() || `Ad ${i + 2}`;
    if (!nonEmpty(entry.name)) {
      out.push(issue(`${field}.name`, 'additional_ad_name_required', 'error', `Name additional ad ${i + 1}.`));
    }
    const entryAssetType = entry.assetType === 'video' ? 'video' : 'image';
    if (!assetPopulated(entry.feedAsset)) {
      out.push(
        issue(
          `${field}.feedAsset`,
          'additional_ad_asset_missing',
          'error',
          `${label}: upload a Feed ${entryAssetType}.`,
        ),
      );
    }
    if (entryAssetType === 'video') {
      if (nonEmpty(entry.feedAsset.url) && !nonEmpty(entry.feedAsset.thumbnailUrl)) {
        out.push(
          issue(
            `${field}.feedAsset`,
            'additional_ad_thumbnail_required',
            'error',
            `${label}: upload a thumbnail for the Feed video — Meta requires one.`,
          ),
        );
      }
      if (nonEmpty(entry.storyAsset.url) && !nonEmpty(entry.storyAsset.thumbnailUrl)) {
        out.push(
          issue(
            `${field}.storyAsset`,
            'additional_ad_thumbnail_required',
            'error',
            `${label}: upload a thumbnail for the Story video — Meta requires one.`,
          ),
        );
      }
    }
    const o = entry.copyOverride;
    if (!nonEmpty(o?.headline ?? '') && !nonEmpty(sharedHeadline)) {
      out.push(
        issue(`${field}.copyOverride.headline`, 'additional_ad_headline_empty', 'error', `${label}: add a headline (shared or override).`),
      );
    }
    if (!nonEmpty(o?.primaryText ?? '') && !nonEmpty(sharedPrimaryText)) {
      out.push(
        issue(`${field}.copyOverride.primaryText`, 'additional_ad_primary_text_empty', 'error', `${label}: add primary text (shared or override).`),
      );
    }
    if (!isLeadGen && !nonEmpty(o?.linkUrl ?? '') && !nonEmpty(sharedUrl)) {
      out.push(
        issue(`${field}.copyOverride.linkUrl`, 'additional_ad_url_required', 'error', `${label}: set a destination URL (shared or override).`),
      );
    }
  });

  return out;
}

// ---------- budget (shared by gate + compose) ----------

type BudgetConfigLike = { amountCents: number; type: 'daily' | 'lifetime' } | null;

// Validate one effective budget slot's VALUE (required / integer / positive /
// suspiciously-high). Returns errors + warnings keyed to `field`.
function checkBudgetValue(
  budget: BudgetConfigLike,
  field: string,
): { errors: MetaAdValidationIssue[]; warnings: MetaAdValidationIssue[] } {
  const errors: MetaAdValidationIssue[] = [];
  const warnings: MetaAdValidationIssue[] = [];
  if (!budget) {
    errors.push(issue(field, 'budget_required', 'error', 'Set a budget.'));
    return { errors, warnings };
  }
  if (!Number.isInteger(budget.amountCents)) {
    errors.push(issue(`${field}.amountCents`, 'budget_not_integer', 'error', 'Budget must be a whole number of cents.'));
  }
  if (budget.amountCents <= 0) {
    errors.push(issue(`${field}.amountCents`, 'budget_not_positive', 'error', 'Budget must be greater than zero.'));
  }
  if (budget.amountCents > BUDGET_SUSPICIOUSLY_HIGH_CENTS) {
    warnings.push(issue(`${field}.amountCents`, 'budget_suspiciously_high', 'warning', 'That budget is over $1,000,000 — double-check the amount.'));
  }
  return { errors, warnings };
}

// Schedule consistency for a resolved (start, end) pair, keyed to a field base.
// Wall-clock values resolve against `tz` (the effective schedule tz) so the
// future-date check uses the intended local instant; the order check is
// tz-independent (both sides share the tz).
function checkSchedule(
  startIso: string | null,
  endIso: string | null,
  nowMs: number,
  tz: string | null,
  startField: string,
  endField: string,
): MetaAdValidationIssue[] {
  const out: MetaAdValidationIssue[] = [];
  const startMs = nonEmpty(startIso) ? wallClockToInstantMs(startIso as string, tz) : null;
  const endMs = nonEmpty(endIso) ? wallClockToInstantMs(endIso as string, tz) : null;
  if (startMs !== null && !Number.isNaN(startMs) && startMs <= nowMs) {
    out.push(issue(startField, 'start_date_in_past', 'error', 'The start date must be in the future.'));
  }
  if (endMs !== null && !Number.isNaN(endMs) && endMs <= nowMs) {
    out.push(issue(endField, 'end_date_in_past', 'error', 'The end date must be in the future.'));
  }
  if (startMs !== null && endMs !== null && !Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs <= startMs) {
    out.push(issue(endField, 'end_date_before_start', 'error', 'The end date must be after the start date.'));
  }
  return out;
}

// ---------- "Add an ad to a live ad set" (attach_to_live_adset) ----------

function validateAttachDraft(draft: MetaAdDraft, ctx: MetaAdValidationContext): ValidationResult {
  const errors: MetaAdValidationIssue[] = [];
  const warnings: MetaAdValidationIssue[] = [];
  const target = draft.attachTarget!;
  const ads = draft.ads;

  if (!nonEmpty(draft.accountId)) {
    errors.push(issue('accountId', 'account_required', 'error', 'Select a client account.'));
  }
  if (!ctx.account || !nonEmpty(ctx.account.fbAdAccountId)) {
    errors.push(issue('accountId', 'account_not_connected', 'error', 'The selected account has no connected Meta ad account.'));
  }
  if (!nonEmpty(target.adSetId) || !nonEmpty(target.campaignId)) {
    errors.push(issue('attachTarget', 'attach_target_missing', 'error', 'Choose the live ad set to add this ad to.'));
  }
  if (!nonEmpty(ads.name)) {
    errors.push(issue('ads.name', 'ad_name_required', 'error', 'Give the new ad a name.'));
  }
  // One new ad, no Advantage+ creative: this path attaches a single standard ad.
  if ((ads.additionalAds ?? []).length > 0 || ads.creative.useDynamicCreative) {
    errors.push(issue('ads', 'attach_single_ad_only', 'error', 'Add one standard ad at a time to a live ad set.'));
  }

  // Media choice (Part A): the source ad must actually offer the reuse that was chosen.
  const mode = mediaModeOf(target);
  if (!modeIsAvailable(mode, target)) {
    errors.push(
      issue(
        'attachTarget.mediaMode',
        'attach_media_unavailable',
        'error',
        mode === 'exact'
          ? 'The source ad has no creative id to reuse. Upload new media instead.'
          : 'The source ad has no original image or video to reuse. Upload new media instead.',
      ),
    );
  }
  if (mode === 'exact') {
    // The creative is attached unchanged: copy, link and form are all baked in, nothing is validated here.
    return { ok: errors.length === 0, errors, warnings };
  }
  if (mode === 'original') {
    const kind = reusableMedia(target).original;
    if (kind && kind !== (ads.creative.assetType === 'video' ? 'video' : 'image')) {
      errors.push(
        issue('ads.creative.assetType', 'attach_media_type_mismatch', 'error', `The original media is a${kind === 'video' ? ' video' : 'n image'}; switch the ad type to match.`),
      );
    }
  }

  // Destination: must agree with what the ad set is (inferred), and be complete.
  const dest = ads.destination;
  if (target.destination === 'unknown') {
    warnings.push(
      issue('ads.destination.type', 'attach_destination_unknown', 'warning', 'We could not tell where this ad set sends people. Choose the destination and make sure it matches the ad set.'),
    );
  } else if (dest.type !== target.destination) {
    errors.push(
      issue(
        'ads.destination.type',
        'attach_destination_mismatch',
        'error',
        target.destination === 'instant_form'
          ? 'This ad set uses an instant form, so the new ad must too.'
          : 'This ad set sends people to a website, so the new ad must too.',
      ),
    );
  }
  if (dest.type === 'instant_form') {
    if (!nonEmpty(dest.leadGenFormId)) {
      errors.push(issue('ads.destination.leadGenFormId', 'leadgenform_required', 'error', 'Choose a lead form for the instant form ad.'));
    }
  } else if (!nonEmpty(dest.url)) {
    errors.push(issue('ads.destination.url', 'url_required', 'error', 'Enter the website link.'));
  } else if (!isValidHttpUrl(dest.url)) {
    errors.push(issue('ads.destination.url', 'url_invalid', 'error', 'The website link must start with http:// or https://.'));
  } else if (isFbMePlaceholder(dest.url)) {
    errors.push(issue('ads.destination.url', 'url_placeholder', 'error', 'http://fb.me/ is a Meta placeholder, not a website link. Enter the real link.'));
  }

  for (const ci of checkCreative(ads.creative)) {
    if (mode === 'original' && ci.code === 'asset_missing') continue; // the original media stands in
    (ci.severity === 'warning' ? warnings : errors).push(ci);
  }
  if (mode === 'new') errors.push(...checkStoryCreative(ads.creative));

  return { ok: errors.length === 0, errors, warnings };
}

// ---------- public: pre-publish gate ----------

export function validateMetaAdDraft(
  draft: MetaAdDraft,
  ctx: MetaAdValidationContext,
): ValidationResult {
  // "Add an ad to a live ad set": ONLY the ad is validated. The campaign / ad set
  // placeholders on such a draft are never sent, so their rules do not apply.
  if (draft.attachTarget) return validateAttachDraft(draft, ctx);

  const errors: MetaAdValidationIssue[] = [];
  const warnings: MetaAdValidationIssue[] = [];
  const { campaign, adSets, ads } = draft;

  // Account binding.
  if (!nonEmpty(draft.accountId)) {
    errors.push(issue('accountId', 'account_required', 'error', 'Select a client account.'));
  }
  if (!ctx.account || !nonEmpty(ctx.account.fbAdAccountId)) {
    errors.push(
      issue('accountId', 'account_not_connected', 'error', 'The selected account has no connected Meta ad account.'),
    );
  }

  // Facebook Page (campaign-wide) — required for the lead-gen page binding + creative Page.
  if (!nonEmpty(campaign.facebookPageId)) {
    errors.push(issue('campaign.facebookPageId', 'page_required', 'error', 'Choose a Facebook Page.'));
  }

  // Conversion pixel (shared creative) — required on both goals (C8).
  if (!nonEmpty(ads.pixelId)) {
    errors.push(issue('ads.pixelId', 'pixel_required', 'error', 'Choose a conversion pixel.'));
  }

  // Campaign-wide: objective × destination, optimization goal + event, shared creative, placements.
  errors.push(...checkGoalDestination(campaign.objective, campaign.destinationType, ads.destination));
  errors.push(...checkOptimizationGoal(campaign.objective, campaign.destinationType, campaign.optimizationGoal, campaign.customEventType));
  for (const ci of checkCreative(ads.creative)) {
    (ci.severity === 'warning' ? warnings : errors).push(ci);
  }
  errors.push(...checkStoryCreative(ads.creative));
  errors.push(...checkPlacements(campaign.placements));
  errors.push(
    ...checkAdditionalAds(ads, campaign.objective === 'LEADS' && campaign.destinationType === 'instant_form'),
  );

  // Budget — CBO (campaign-level) vs ABO (per ad set).
  const cbo = campaign.budgetMode === 'CBO';
  if (cbo) {
    const { errors: be, warnings: bw } = checkBudgetValue(campaign.budget, 'campaign.budget');
    errors.push(...be);
    warnings.push(...bw);
  } else if (campaign.budget !== null) {
    // ABO selected but a campaign budget is set — clear it.
    errors.push(issue('campaign.budget', 'budget_mismatch_abo', 'error', 'Ad-set budget (ABO) is selected — clear the campaign budget.'));
  }

  // Campaign default schedule (the inherited dates) — checked once. Wall-clock
  // resolves against the effective tz (account tz, or Australian Eastern on override).
  const nowMs = ctx.now.getTime();
  const scheduleTz = effectiveScheduleTimeZone(
    campaign.scheduleUseAustralianEastern === true,
    ctx.scheduleTimezone ?? null,
  );
  errors.push(
    ...checkSchedule(campaign.defaultStartDate, campaign.defaultEndDate, nowMs, scheduleTz, 'campaign.defaultStartDate', 'campaign.defaultEndDate'),
  );
  // CBO lifetime needs an end on the campaign schedule.
  if (cbo && campaign.budget?.type === 'lifetime' && !nonEmpty(campaign.defaultEndDate)) {
    errors.push(issue('campaign.defaultEndDate', 'lifetime_requires_end_date', 'error', 'A lifetime budget needs an end date.'));
  }

  // At least one ad set.
  if (adSets.length === 0) {
    errors.push(issue('adSets', 'adsets_empty', 'error', 'Add at least one ad set.'));
  }

  // Per ad set: targeting, ABO budget, date override + lifetime end.
  adSets.forEach((adSet, i) => {
    const base = `adSets.${i}`;
    // Targeting. Extracted targeting is LOCKED — Meta's verbatim shape is authoritative.
    if (adSet.targeting.sourceType !== 'extracted') {
      errors.push(...withAdSetId(checkTargeting(adSet.targeting, `${base}.targeting`), adSet.id));
    }

    // Budget.
    if (cbo) {
      if (adSet.budget !== null) {
        errors.push(
          ...withAdSetId(
            [issue(`${base}.budget`, 'budget_mismatch_cbo', 'error', 'Campaign budget (CBO) is selected — clear the ad-set budget.')],
            adSet.id,
          ),
        );
      }
    } else {
      const { errors: be, warnings: bw } = checkBudgetValue(adSet.budget, `${base}.budget`);
      errors.push(...withAdSetId(be, adSet.id));
      warnings.push(...withAdSetId(bw, adSet.id));
    }

    // Schedule — only when this ad set overrides the campaign default (the default
    // itself is validated once above). Effective dates drive the lifetime-end check.
    const dates = resolveAdSetDates(adSet, campaign);
    if (adSet.datesOverridden) {
      errors.push(
        ...withAdSetId(
          checkSchedule(adSet.startDate, adSet.endDate, nowMs, scheduleTz, `${base}.startDate`, `${base}.endDate`),
          adSet.id,
        ),
      );
    }
    // ABO lifetime needs the effective end date.
    const effectiveBudget = cbo ? campaign.budget : adSet.budget;
    if (!cbo && effectiveBudget?.type === 'lifetime' && !nonEmpty(dates.endDate)) {
      errors.push(
        ...withAdSetId(
          [issue(`${base}.endDate`, 'lifetime_requires_end_date', 'error', 'A lifetime budget needs an end date.')],
          adSet.id,
        ),
      );
    }
  });

  return { ok: errors.length === 0, errors, warnings };
}

// ---------- public: compose-time checklist (non-blocking) ----------

export function validateForCompose(input: MetaAdDraftInput): ValidationWarnings {
  const out: MetaAdValidationIssue[] = [];
  const { campaign, adSets, ads } = input;

  // "Add an ad to a live ad set": only the ad is composed; the campaign / ad set
  // placeholders have no budget, page, pixel or audience to nudge about.
  if (input.attachTarget) {
    const d = ads.destination;
    if (!nonEmpty(ads.name)) out.push(issue('ads.name', 'ad_name_required', 'warning', 'Name the new ad.'));
    if (d.type === 'instant_form' && !nonEmpty(d.leadGenFormId)) {
      out.push(issue('ads.destination.leadGenFormId', 'leadgenform_required', 'warning', 'Choose a lead form.'));
    }
    if (d.type === 'website' && !nonEmpty(d.url)) {
      out.push(issue('ads.destination.url', 'url_required', 'warning', 'Enter the website link.'));
    }
    if ((ads.creative.headlines ?? []).filter((h) => nonEmpty(h)).length < 1) {
      out.push(issue('ads.creative.headlines', 'headlines_empty', 'warning', 'Add a headline.'));
    }
    if ((ads.creative.primaryTexts ?? []).filter((p) => nonEmpty(p)).length < 1) {
      out.push(issue('ads.creative.primaryTexts', 'primary_text_empty', 'warning', 'Add primary text.'));
    }
    if (!assetPopulated(ads.creative.feedAsset)) {
      out.push(issue('ads.creative.feedAsset', 'asset_missing', 'warning', 'Upload a new image or video.'));
    }
    return out;
  }

  if (!nonEmpty(input.accountId)) {
    out.push(issue('accountId', 'account_required', 'warning', 'Pick a client to publish to.'));
  }
  // Budget for the chosen mode (compose tolerates missing).
  const cbo = campaign.budgetMode === 'CBO';
  if (cbo) {
    if (!campaign.budget) out.push(issue('campaign.budget', 'budget_required', 'warning', 'Set a campaign budget.'));
  } else {
    adSets.forEach((adSet, i) => {
      if (!adSet.budget) {
        out.push({ ...issue(`adSets.${i}.budget`, 'budget_required', 'warning', 'Set an ad set budget.'), adSetId: adSet.id });
      }
    });
  }
  if (!nonEmpty(campaign.facebookPageId)) {
    out.push(issue('campaign.facebookPageId', 'page_required', 'warning', 'Choose a Facebook Page.'));
  }
  if (!nonEmpty(ads.pixelId)) {
    out.push(issue('ads.pixelId', 'pixel_required', 'warning', 'Choose a conversion pixel.'));
  }
  // Per ad set: geo nudge (skip extracted — geo lives in the verbatim payload).
  adSets.forEach((adSet, i) => {
    if (adSet.targeting.sourceType !== 'extracted') {
      out.push(...withAdSetId(checkGeoLevel(adSet.targeting, 'warning', `adSets.${i}.targeting`), adSet.id));
    }
  });
  if ((ads.creative.headlines ?? []).filter((h) => nonEmpty(h)).length < 1) {
    out.push(issue('ads.creative.headlines', 'headlines_empty', 'warning', 'Add a headline.'));
  }
  if ((ads.creative.primaryTexts ?? []).filter((p) => nonEmpty(p)).length < 1) {
    out.push(issue('ads.creative.primaryTexts', 'primary_text_empty', 'warning', 'Add primary text.'));
  }
  const c = ads.creative;
  const hasFeedAsset = assetPopulated(c.feedAsset);
  if (!hasFeedAsset) {
    out.push(issue('ads.creative.feedAsset', 'asset_missing', 'warning', 'Upload a Feed asset.'));
  }
  // D3.4: the Story asset must match the Feed asset type.
  const hasStoryAsset = nonEmpty(c.storyAsset.url);
  if (hasStoryAsset && c.storyAsset.type && c.storyAsset.type !== c.assetType) {
    out.push(
      issue('ads.creative.storyAsset', 'story_asset_type_mismatch', 'warning', 'The Story asset must be the same type (image or video) as the Feed asset.'),
    );
  }
  // D3.1: manual Story placements selected but no Story asset → dropped at publish.
  const pl = campaign.placements;
  if (pl.mode === 'manual' && pl.manualConfig && hasAnyStoryPosition(pl.manualConfig) && !hasStoryAsset) {
    out.push(
      issue('campaign.placements', 'placements_story_without_image', 'warning', 'Story placements are selected but no Story asset (9:16) is set — they’ll be dropped at publish.'),
    );
  }

  return out;
}
